'use client'

import { ChevronDown, List } from 'lucide-react'
import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useId, useMemo, useState } from 'react'

import {
  scrollToHeading,
  TOC_SCROLLBAR_CLASSES,
  TocNestedList,
  TocSemrushHeading,
  useActiveTocId,
} from '@/components/Blog/tableOfContentsShared'
import type { TocEntry } from '@/utilities/extractTocFromLexical'
import { buildTocTree } from '@/utilities/extractTocFromLexical'
import { cn } from '@/utilities/ui'

/** Número de H2s visíveis antes do "Mostrar índice completo". */
const TOC_PREVIEW_H2S = 5

type WidgetTocConfig = {
  tocTitle?: string | null
  showTitle?: boolean | null
  collapsible?: boolean | null
  defaultOpen?: boolean | null
}

type Props = {
  widgetTitle: string
  entries: readonly TocEntry[]
  config?: WidgetTocConfig | null
  hideBelowLg?: boolean
}

/**
 * TOC sidebar redesenhado — melhor que Semrush:
 *
 * Desktop:
 *  · Indicador de posição via borda esquerda colorida (estilo Stripe/Vercel docs)
 *  · Hierarquia sempre visível: sem accordion — H3/H4 aparecem sempre
 *  · Auto-expande se o item ativo estiver fora do preview truncado
 *  · Preview de 5 H2s (vs 3 do Semrush) com "Mostrar índice completo"
 *  · Zero scrollbar no desktop — altura natural do conteúdo
 *
 * Mobile:
 *  · Cabeçalho colapsável com ChevronDown
 *  · Lista com scroll limitado e scrollbar fino
 */
export function TableOfContentsWidget({ widgetTitle, entries, config, hideBelowLg }: Props) {
  const t = useTranslations('Sidebar')
  const showTitle = config?.showTitle !== false
  const collapsible = config?.collapsible !== false
  const defaultOpen = config?.defaultOpen !== false
  const sectionLabel = (config?.tocTitle?.trim() || widgetTitle).trim() || t('tocFallbackTitle')

  const [mobileOpen, setMobileOpen] = useState(defaultOpen)
  const [desktopTruncated, setDesktopTruncated] = useState(true)
  const navId = useId()
  const activeId = useActiveTocId(entries, true)
  const tree = useMemo(() => buildTocTree(entries), [entries])

  const visibleNodes = useMemo(
    () => (desktopTruncated ? tree.slice(0, TOC_PREVIEW_H2S) : tree),
    [tree, desktopTruncated],
  )

  useEffect(() => {
    setMobileOpen(defaultOpen)
  }, [defaultOpen, entries])

  const onActivate = useCallback((id: string) => {
    scrollToHeading(id)
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches) {
      setMobileOpen(false)
    }
  }, [])

  const handleDesktopToggle = useCallback(() => {
    setDesktopTruncated((t) => !t)
  }, [])

  if (entries.length === 0) return null

  const showToggleBtn = tree.length > TOC_PREVIEW_H2S

  return (
    <section className={cn('toc-widget', hideBelowLg && 'hidden lg:block')}>

      {/* ── Mobile ─────────────────────────────────────────────────────── */}
      {collapsible ? (
        /* Recolhível (default): pill button + dropdown (estilo Semrush). */
        <div className="relative z-20 mb-4 lg:hidden">
          {/* Overlay leve atrás do dropdown */}
          <div
            aria-hidden
            onClick={() => setMobileOpen(false)}
            className={cn(
              'fixed inset-0 z-[19] bg-black/25 transition-opacity duration-200',
              mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
            )}
          />

          {/* Pill button */}
          <button
            type="button"
            aria-expanded={mobileOpen}
            aria-controls={navId}
            onClick={() => setMobileOpen((o) => !o)}
            className={cn(
              'relative z-[21] flex w-full items-center gap-2.5 rounded-full border border-border bg-background px-4 py-2.5',
              'text-sm font-semibold text-foreground transition-colors hover:bg-muted/40',
            )}
          >
            <List className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <span className="flex-1 text-left">{sectionLabel}</span>
            <ChevronDown
              className={cn(
                'size-4 shrink-0 text-muted-foreground transition-transform duration-200 ease-out',
                mobileOpen && 'rotate-180',
              )}
              aria-hidden
            />
          </button>

          {/* Dropdown — sem borda nem sombra no widget */}
          <div
            id={navId}
            aria-hidden={!mobileOpen}
            className={cn(
              'absolute left-0 right-0 z-[21] mt-1.5 overflow-hidden rounded-xl bg-background',
              'grid transition-[grid-template-rows,opacity] duration-200 ease-out',
              mobileOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0 pointer-events-none',
            )}
          >
            <div className="min-h-0">
              <div className={cn('max-h-[min(60vh,24rem)] px-2 py-3', TOC_SCROLLBAR_CLASSES)}>
                <TocNestedList nodes={tree} activeId={activeId} depth={0} onActivate={onActivate} />
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* "Recolhível" desligado no admin: lista sempre aberta, sem pill/toggle. */
        <div className="mb-4 lg:hidden">
          {showTitle && (
            <div className="mb-2 flex items-center gap-2.5 px-1 text-sm font-semibold text-foreground">
              <List className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              <span className="flex-1 text-left">{sectionLabel}</span>
            </div>
          )}
          <div className={cn('max-h-[min(60vh,24rem)] px-2 py-1', TOC_SCROLLBAR_CLASSES)}>
            <TocNestedList nodes={tree} activeId={activeId} depth={0} onActivate={onActivate} />
          </div>
        </div>
      )}

      {/* ── Desktop ─────────────────────────────────────────────────────── */}
      <div className="hidden lg:flex lg:flex-col lg:gap-3">
        {showTitle && (
          <TocSemrushHeading className="pl-3">{sectionLabel}</TocSemrushHeading>
        )}

        <TocNestedList
          nodes={visibleNodes}
          activeId={activeId}
          depth={0}
          onActivate={onActivate}
        />

        {showToggleBtn && (
          <button
            type="button"
            data-test="toc-collapse-button"
            className="ml-3 cursor-pointer border-0 bg-transparent p-0 text-left text-[12px] text-muted-foreground underline decoration-1 underline-offset-2 transition-colors hover:text-foreground"
            onClick={handleDesktopToggle}
          >
            {desktopTruncated ? t('tocShowFull') : t('tocHide')}
          </button>
        )}
      </div>
    </section>
  )
}
